import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import api from '../services/api';
import { toast } from 'sonner';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(null);
  const [cartId, setCartId] = useState(null);
  const [loading, setLoading] = useState(false);

  // Always-current cart snapshot + a serialized op queue so rapid consecutive
  // cart mutations chain instead of racing on stale React state.
  const cartRef = useRef(null);
  const opChain = useRef(Promise.resolve());
  useEffect(() => { cartRef.current = cart; }, [cart]);

  const runQueued = (fn) => {
    const next = opChain.current.then(fn, fn);
    opChain.current = next.catch(() => {});
    return next;
  };

  const commitCart = (c) => {
    cartRef.current = c;
    setCart(c);
    setCartId(c.id);
    localStorage.setItem('prsvr_cart_id', c.id);
    localStorage.setItem('prsvr_cart_url', c.webUrl);
  };

  // Load cart from localStorage on mount
  useEffect(() => {
    const savedCartId = localStorage.getItem('prsvr_cart_id');
    if (savedCartId) {
      setCartId(savedCartId);
      // We'll fetch cart details if needed
    }
  }, []);

  const addToCart = async (variantId, quantity = 1, productName = '') => {
    try {
      setLoading(true);

      // If cart exists, we need to add to existing cart, not create new one
      if (cartId && cart) {
        // Check if item already in cart (same variant)
        const existingItem = cart.lineItems?.find(item => item.variantId === variantId);
        
        // Build line items array with existing items
        const lineItems = cart.lineItems?.map(item => ({
          variantId: item.variantId, // Use consistent field name
          quantity: item.quantity
        })) || [];
        
        if (existingItem) {
          // Update quantity of existing item
          const itemIndex = lineItems.findIndex(item => item.variantId === variantId);
          lineItems[itemIndex].quantity += quantity;
        } else {
          // Add new item (different variant)
          lineItems.push({
            variantId: variantId,
            quantity: quantity
          });
        }
        
        const checkoutData = { lineItems };
        console.log('Updating existing cart with line items:', lineItems);
        const response = await api.post('/shopify/checkout', checkoutData);
        
        if (response.data.success && response.data.checkout) {
          const updatedCart = response.data.checkout;
          setCart(updatedCart);
          setCartId(updatedCart.id);
          localStorage.setItem('prsvr_cart_id', updatedCart.id);
          localStorage.setItem('prsvr_cart_url', updatedCart.webUrl);
          
          toast.success(`Added ${productName || 'item'} to cart!`);
          return true;
        }
      } else {
        // Create new cart with first item
        const checkoutData = {
          lineItems: [
            {
              variantId: variantId,
              quantity: quantity
            }
          ]
        };

        console.log('Creating new cart with line items:', checkoutData.lineItems);
        const response = await api.post('/shopify/checkout', checkoutData);
        
        if (response.data.success && response.data.checkout) {
          const newCart = response.data.checkout;
          setCart(newCart);
          setCartId(newCart.id);
          localStorage.setItem('prsvr_cart_id', newCart.id);
          localStorage.setItem('prsvr_cart_url', newCart.webUrl);
          
          toast.success(`Added ${productName || 'item'} to cart!`);
          return true;
        }
      }
      
      toast.error('Failed to add to cart');
      return false;
    } catch (error) {
      console.error('Error adding to cart:', error);
      toast.error('Failed to add to cart. Please try again.');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const goToCheckout = (discountCode) => {
    const cartUrl = localStorage.getItem('prsvr_cart_url');
    if (cartUrl) {
      let url = cartUrl;
      if (discountCode) {
        url += (url.includes('?') ? '&' : '?') + 'discount=' + encodeURIComponent(discountCode);
      }
      window.location.href = url;
    } else {
      toast.error('No items in cart');
    }
  };

  // Add multiple variants to the Shopify-connected cart in one request.
  const addManyToCart = (items) => runQueued(async () => {
    if (!items || items.length === 0) return false;
    try {
      setLoading(true);
      const merged = (cartRef.current?.lineItems || []).map((i) => ({
        variantId: i.variantId,
        quantity: i.quantity,
      }));
      items.forEach(({ variantId, quantity = 1 }) => {
        const idx = merged.findIndex((m) => m.variantId === variantId);
        if (idx >= 0) merged[idx].quantity += quantity;
        else merged.push({ variantId, quantity });
      });

      const response = await api.post('/shopify/checkout', { lineItems: merged });
      if (response.data.success && response.data.checkout) {
        commitCart(response.data.checkout);
        toast.success(items.length > 1 ? `Added ${items.length} pieces to your rack` : 'Added to your rack');
        return true;
      }
      toast.error('Failed to add to cart');
      return false;
    } catch (error) {
      console.error('Error adding many to cart:', error);
      toast.error('Failed to add to cart. Please try again.');
      return false;
    } finally {
      setLoading(false);
    }
  });

  // Remove a variant from the Shopify-connected cart.
  const removeLineItem = (variantId) => runQueued(async () => {
    const remaining = (cartRef.current?.lineItems || [])
      .filter((i) => i.variantId !== variantId)
      .map((i) => ({ variantId: i.variantId, quantity: i.quantity }));
    if (remaining.length === 0) {
      clearCart();
      return true;
    }
    try {
      setLoading(true);
      const response = await api.post('/shopify/checkout', { lineItems: remaining });
      if (response.data.success && response.data.checkout) {
        commitCart(response.data.checkout);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error removing item:', error);
      return false;
    } finally {
      setLoading(false);
    }
  });

  const getCartCount = () => {
    if (!cart || !cart.lineItems) return 0;
    return cart.lineItems.reduce((total, item) => total + item.quantity, 0);
  };

  const clearCart = () => {
    cartRef.current = null;
    setCart(null);
    setCartId(null);
    localStorage.removeItem('prsvr_cart_id');
    localStorage.removeItem('prsvr_cart_url');
  };

  const value = {
    cart,
    cartId,
    loading,
    addToCart,
    addManyToCart,
    removeLineItem,
    goToCheckout,
    getCartCount,
    clearCart
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
